import React, { useEffect, useRef, useState } from 'react';
import AudioBlock from '../../../components/features/audioFeatures/audioBlock';
import TranscriptBlock from '../../../components/features/audioFeatures/TranscriptBlock';
import { useNoteState } from '../../../context/noteContexts/noteContext';
import { useColorContext } from '../../../context/colorContext';

const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

const toSafeHex = (hex, fallback) => {
const value = String(hex || '').trim();
return /^#([A-Fa-f0-9]{6})$/.test(value) ? value : fallback;
};

const hexToRgba = (hex, alpha, fallback = '#000000') => {
const safeHex = toSafeHex(hex, fallback);
const r = parseInt(safeHex.slice(1, 3), 16);
const g = parseInt(safeHex.slice(3, 5), 16);
const b = parseInt(safeHex.slice(5, 7), 16);
return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const resolveAudioSrc = (audioUrl) => {
const raw = String(audioUrl || '').trim();
if (!raw) return '';
if (/^https?:\/\//i.test(raw)) return raw;
if (raw.startsWith('/')) return `${BACKEND_BASE_URL}${raw}`;
return `${BACKEND_BASE_URL}/${raw}`;
};

const resolveAudioSrcFromItem = (audioItem) => {
const direct = resolveAudioSrc(audioItem?.audioUrl);
if (direct) return direct;

const fileName = String(audioItem?.audioFileName || '').trim();
if (!fileName) return '';
return `${BACKEND_BASE_URL}/cache/podcast-audio/${encodeURIComponent(fileName)}`;
};

const AudioNote = ({
titles: titlesProp,
audioBlocksJson: audioBlocksJsonProp,
className = '',
titleClassName = '',
sectionClassName = '',
}) => {
const { audioBlockTitles, audioBlockJson } = useNoteState();
const { bgSecondary, textPrimary, textSecondary, activeAccent } = useColorContext();

const safeTextPrimary = toSafeHex(textPrimary, '#F0F0F0');
const safeTextSecondary = toSafeHex(textSecondary, '#BDBDBD');
const safeAccent = toSafeHex(activeAccent, '#3B82F6');
const safeMenuBg = toSafeHex(bgSecondary, '#181C22');
const safeChipBg = hexToRgba(safeTextPrimary, 0.12, safeMenuBg);

const titles = Array.isArray(titlesProp)
? titlesProp
: (Array.isArray(audioBlockTitles) ? audioBlockTitles : []);
const audioBlocksJson = Array.isArray(audioBlocksJsonProp)
? audioBlocksJsonProp
: (Array.isArray(audioBlockJson) ? audioBlockJson : []);
const itemCount = Math.max(titles.length, audioBlocksJson.length);

const [playingIndex, setPlayingIndex] = useState(-1);
const [currentTimes, setCurrentTimes] = useState({});
const blockRefs = useRef({});

// Stable per-index ref callback so React never triggers null/element dance on re-renders
const blockRefCbs = useRef({});
const getBlockRef = (index) => {
	if (!blockRefCbs.current[index]) {
		blockRefCbs.current[index] = (el) => { blockRefs.current[index] = el; };
	}
	return blockRefCbs.current[index];
};

useEffect(() => () => {
	Object.values(blockRefs.current).forEach((b) => b?.pause());
}, []);

if (itemCount === 0) {
return (
<div
className={className}
style={{
maxWidth: 1500,
margin: '0 auto',
padding: '22px 8px 8px',
}}
>
<p style={{ color: safeTextSecondary }}>No audio block available.</p>
</div>
);
}

return (
<div
className={className}
style={{
maxWidth: 1500,
margin: '0 auto',
display: 'flex',
flexDirection: 'column',
gap: 32,
padding: '2px 12px 20px',
}}
>
{Array.from({ length: itemCount }).map((_, index) => {
const title = titles[index] || `Audio ${index + 1}`;
const audioItem = audioBlocksJson[index] || null;
const audioSrc = resolveAudioSrcFromItem(audioItem);
const sectionTitle = String(audioItem?.title || '').trim() || title;
const isPodcastSection = String(sectionTitle || '').trim().toLowerCase() === 'podcast' || String(title || '').trim().toLowerCase() === 'podcast';
const audioItemForBlock = audioItem ? { ...audioItem, title } : audioItem;

const sectionStyle = {
padding: '8px 0 0',
marginBottom: 0,
background: 'transparent',
border: 'none',
boxShadow: 'none',
position: 'relative',
overflow: 'visible',
};

const titleStyle = {
  margin: 0,
  fontSize: '1.32rem',
  fontWeight: 780,
  letterSpacing: '-0.01em',
  color: safeTextPrimary,
  padding: 0,
  textShadow: 'none',
  background: 'none',
  textAlign: isPodcastSection ? 'center' : 'left',
  width: isPodcastSection ? '100%' : 'auto',
};

const cardHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: isPodcastSection ? 'center' : 'space-between',
  gap: 16,
  marginBottom: 22,
  padding: '4px 4px 0',
};

const chipStyle = {
padding: '8px 16px',
borderRadius: 999,
fontSize: '0.75rem',
fontWeight: 720,
letterSpacing: '0.08em',
textTransform: 'uppercase',
color: hexToRgba(safeTextPrimary, 0.95),
background: safeChipBg,
boxShadow: 'none',
whiteSpace: 'nowrap',
backdropFilter: 'blur(10px)',
WebkitBackdropFilter: 'blur(10px)',
fontVariantNumeric: 'tabular-nums',
};

if (!audioItem) {
return (
<section key={`audio-note-empty-${index}`} className={sectionClassName} style={sectionStyle}>
<div style={cardHeaderStyle}>
  <h3 className={titleClassName} style={titleStyle}>{sectionTitle}</h3>
  {!isPodcastSection && <span style={chipStyle}>Section {index + 1}</span>}
</div>
							<p style={{ color: safeTextSecondary, fontSize: '0.95rem' }}>No audio data for this title.</p>
</section>
);
}

return (
<section key={`audio-note-${index}`} className={sectionClassName} style={sectionStyle}>
<div style={cardHeaderStyle}>
  <h3 className={titleClassName} style={titleStyle}>{sectionTitle}</h3>
  {!isPodcastSection && <span style={chipStyle}>Section {index + 1}</span>}
</div>

{Array.isArray(audioItemForBlock?.dialogueTurns) && audioItemForBlock.dialogueTurns.length > 0 && (
  <div style={{ marginBottom: 20 }}>
    <TranscriptBlock
      audioBlockItem={audioItemForBlock}
      currentTimeSeconds={Number(currentTimes[index]) || 0}
      isPlaying={playingIndex === index}
      color={safeAccent}
      backgroundColor='transparent'
      borderColor='transparent'
      textColor={safeTextPrimary}
      accentColor={safeTextSecondary}
    />
  </div>
)}

<AudioBlock
  ref={getBlockRef(index)}
  audioSrc={audioSrc}
  audioBlockItem={audioItemForBlock}
  audioName={title}
  totalDurationSeconds={Number(audioItem?.durationSeconds) || 0}
  color={safeAccent}
  backgroundColor='transparent'
  audioPartBackgroundColor={safeChipBg}
  borderColor='transparent'
  padding='0'
  textColor={safeTextPrimary}
  accentColor={safeTextSecondary}
  onPlayStart={() => {
    Object.entries(blockRefs.current).forEach(([i, b]) => {
      if (Number(i) !== index) b?.pause();
    });
  }}
  onPlayStateChange={(playing) => {
    setPlayingIndex(playing ? index : (prev) => (prev === index ? -1 : prev));
  }}
  onTimeUpdate={(t) => {
    setCurrentTimes((prev) => ({ ...prev, [index]: t }));
  }}
/>
</section>
);
})}
</div>
);
};

export default AudioNote;
